import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  MenuItem,
  Grid,
} from '@mui/material';
import axios from 'axios';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'PLAYER',
    phone: '',
    dateOfBirth: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
      await axios.post(`${apiUrl}/register`, {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        phone: formData.phone || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Registration failed');
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2a2a2a 100%)',
          py: 4,
        }}
      >
        <Container component="main" maxWidth="xs">
          <Paper elevation={8} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
            <Box
              component="img"
              src="/logo.bmp"
              alt="GKM Tennis Academy"
              sx={{
                width: 100,
                height: 'auto',
                mb: 3,
                mx: 'auto',
                display: 'block',
                borderRadius: '8px',
              }}
            />
            <Typography component="h1" variant="h4" gutterBottom color="success.main" fontWeight={700}>
              ✓ Registration Successful!
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
              We've sent a verification link to your email address. Please check your inbox and
              click the link to verify your account.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After verifying, your account will be reviewed by an administrator. You will be able
              to log in once it's approved. Don't forget to check your spam folder.
            </Typography>
            <Typography variant="body2" sx={{ mt: 3 }} color="primary">
              Redirecting to login page...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2a2a2a 100%)',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            padding: 4,
            width: '100%',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/logo.bmp"
              alt="GKM Tennis Academy"
              sx={{
                width: 100,
                height: 'auto',
                mb: 2,
                borderRadius: '8px',
              }}
            />
            <Typography component="h1" variant="h4" align="center" fontWeight={700} color="primary">
              GKM Tennis Academy
            </Typography>
            <Typography component="h2" variant="body1" align="center" color="text.secondary" sx={{ mt: 1 }}>
              Register as Player or Parent
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  disabled={loading}
                  helperText="Min 8 characters, must include uppercase, lowercase, number, and special character"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  select
                  label="I am a..."
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="PLAYER">Player</MenuItem>
                  <MenuItem value="PARENT">Parent</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  helperText="Optional, but recommended for players"
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Register'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link to="/login" style={{ textDecoration: 'none', color: '#2563eb' }}>
                Already have an account? Sign In
              </Link>
            </Box>
          </Box>

          <Box sx={{ mt: 3, p: 2.5, bgcolor: 'primary.main', borderRadius: 2, color: 'white' }}>
            <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 1 }}>
              📋 Note About Registration
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
              Your account will be pending approval after registration.
            </Typography>
            <Typography variant="caption" component="div">
              An administrator will review and approve your registration. You will receive an email
              notification once approved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
